/**
 * The admin panel's tab bar.
 *
 * Written by hand rather than reusing `SegmentedControl`, which is documented
 * for two to four exclusive options and is a radio group: it selects on arrow
 * press, which is right for a filter and wrong for navigation, where arrowing
 * past four panels to reach the fifth would load each one on the way.
 *
 * This follows the ARIA tabs pattern instead — arrows move focus, Enter or
 * Space selects — so a keyboard user can look before they leap.
 */

import { useRef, type KeyboardEvent } from "react";

export interface AdminTab<T extends string> {
  id: T;
  label: string;
}

interface AdminTabsProps<T extends string> {
  tabs: AdminTab<T>[];
  active: T;
  onChange: (id: T) => void;
}

export function AdminTabs<T extends string>({
  tabs,
  active,
  onChange,
}: AdminTabsProps<T>) {
  const refs = useRef(new Map<T, HTMLButtonElement>());

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = tabs.findIndex((tab) => tab.id === active);
    if (index === -1) return;

    const step =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    let next: number | null = null;

    if (step !== 0) next = (index + step + tabs.length) % tabs.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = tabs.length - 1;
    if (next === null) return;

    event.preventDefault();
    /*
     * Focus moves; selection does not. Selecting on arrow would mount every
     * panel between here and the destination, and each of these fetches.
     */
    refs.current.get(tabs[next]!.id)?.focus();
  }

  return (
    <div
      className="fc-admin__tabs"
      role="tablist"
      aria-label="Admin sections"
      onKeyDown={onKeyDown}
    >
      {tabs.map((tab) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            ref={(node) => {
              if (node) refs.current.set(tab.id, node);
              else refs.current.delete(tab.id);
            }}
            type="button"
            role="tab"
            id={`fc-admin-tab-${tab.id}`}
            aria-controls={`fc-admin-panel-${tab.id}`}
            aria-selected={selected}
            // Only the selected tab is in the tab order: the group is one stop,
            // and arrows move within it.
            tabIndex={selected ? 0 : -1}
            className="fc-admin__tab"
            data-selected={selected || undefined}
            onClick={() => onChange(tab.id)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/** The panel half of the pair. Keeps the ARIA wiring in one place. */
export function AdminTabPanel({
  id,
  active,
  children,
}: {
  id: string;
  active: boolean;
  children: React.ReactNode;
}) {
  if (!active) return null;
  return (
    <div
      role="tabpanel"
      id={`fc-admin-panel-${id}`}
      aria-labelledby={`fc-admin-tab-${id}`}
    >
      {children}
    </div>
  );
}
